import React, { useState } from "react";
import { LuX, LuImage } from "react-icons/lu";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../utils/firebase";

const fieldCls = "w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-ring transition-colors bg-card";
const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";

function AddProductModal({ onClose, onAdd }) {
  const [formData,    setFormData]    = useState({ name: "", price: "", quantity: "", category: "", description: "", variantsCsv: "" });
  const [images,      setImages]      = useState([]);
  const [heroImage,   setHeroImage]   = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [heroActive,  setHeroActive]  = useState(false);
  const [uploading,   setUploading]   = useState(false);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleHeroChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setHeroImage(file);
    setHeroPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const variants = formData.variantsCsv.split(",").map((v) => v.trim()).filter(Boolean);
      const productRef = await addDoc(collection(db, "products"), {
        name:        formData.name,
        price:       parseFloat(formData.price),
        quantity:    parseInt(formData.quantity, 10),
        category:    formData.category,
        description: formData.description,
        variants,
        imageUrls:   [],
        heroActive:  false,
        heroImageUrl: null,
      });

      // Upload gallery images
      const imageUrls = [];
      for (const image of images) {
        const storageRef = ref(storage, `productImages/${productRef.id}/${Date.now()}_${image.name}`);
        await uploadBytes(storageRef, image, { contentType: image.type || "application/octet-stream" });
        imageUrls.push(await getDownloadURL(storageRef));
      }

      // Upload hero banner if provided
      let heroImageUrl = null;
      if (heroImage) {
        const heroRef = ref(storage, `heroBanners/${productRef.id}/${Date.now()}_${heroImage.name}`);
        await uploadBytes(heroRef, heroImage, { contentType: heroImage.type || "image/jpeg" });
        heroImageUrl = await getDownloadURL(heroRef);
      }

      await updateDoc(doc(db, "products", productRef.id), { imageUrls, heroActive, heroImageUrl });
      onAdd();
      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to add product.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-border">
          <h2 className="text-lg font-black text-foreground">Add New Product</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors text-[var(--color-muted-foreground)]">
            <LuX size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Product Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Elf Bar 5000" required className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Category *</label>
              <input type="text" name="category" value={formData.category} onChange={handleChange} placeholder="e.g. Disposable Vapes" required className={fieldCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Price ($) *</label>
              <input type="number" name="price" value={formData.price} onChange={handleChange} placeholder="0.00" step="0.01" min="0" required className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Quantity *</label>
              <input type="number" name="quantity" value={formData.quantity} onChange={handleChange} placeholder="0" min="0" required className={fieldCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Product description…" rows={3} className={`${fieldCls} resize-none`} />
          </div>

          <div>
            <label className={labelCls}>Variants (comma separated)</label>
            <input type="text" name="variantsCsv" value={formData.variantsCsv} onChange={handleChange} placeholder="Blue Razz, Mango Ice, Strawberry" className={fieldCls} />
          </div>

          <div>
            <label className={labelCls}>Product Images</label>
            <div className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-accent transition-colors cursor-pointer">
              <input type="file" multiple accept="image/*" onChange={(e) => setImages(Array.from(e.target.files || []))} className="hidden" id="add-images" />
              <label htmlFor="add-images" className="cursor-pointer">
                <LuImage size={32} className="text-[var(--color-muted-foreground)] opacity-40 mx-auto mb-2" />
                <p className="text-sm text-[var(--color-muted-foreground)]">{images.length > 0 ? `${images.length} file(s) selected` : "Click to upload images"}</p>
              </label>
            </div>
          </div>

          {/* Hero banner */}
          <div className="bg-accent/5 dark:bg-accent/10 rounded-xl p-4 space-y-3 border border-accent/10">
            <div>
              <label className={labelCls}>Hero Banner (Homepage Carousel)</label>
              <p className="text-xs text-[var(--color-muted-foreground)] mb-2">Wide landscape image shown in the homepage hero. Recommended: 1600×900px or wider.</p>
              <div className="border-2 border-dashed border-accent/20 rounded-xl p-3 text-center hover:border-accent transition-colors cursor-pointer">
                <input type="file" accept="image/*" onChange={handleHeroChange} className="hidden" id="add-hero" />
                <label htmlFor="add-hero" className="cursor-pointer">
                  {heroPreview ? (
                    <img src={heroPreview} alt="Hero preview" className="w-full h-28 object-cover rounded-lg" />
                  ) : (
                    <>
                      <LuImage size={28} className="text-accent opacity-30 mx-auto mb-1" />
                      <p className="text-sm text-[var(--color-muted-foreground)]">Upload hero banner (wide)</p>
                    </>
                  )}
                </label>
              </div>
              {heroPreview && (
                <button type="button" onClick={() => { setHeroImage(null); setHeroPreview(null); }} className="mt-1.5 text-xs text-red-400 hover:text-red-600 font-medium">
                  Remove
                </button>
              )}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={heroActive}
                onChange={(e) => setHeroActive(e.target.checked)}
                className="w-4 h-4 accent-brand-navy"
              />
              <span className="text-sm font-semibold text-foreground">Feature in homepage hero carousel</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={uploading} className="flex-1 border border-border text-foreground font-bold py-3 rounded-xl hover:bg-muted transition-colors text-sm">
              Cancel
            </button>
            <button type="submit" disabled={uploading} className="flex-1 bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold py-3 rounded-xl text-sm disabled:opacity-60">
              {uploading ? "Uploading…" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddProductModal;
